import { EMPTY, typeOf } from '../game/types.js';
import { SQUARE_COUNT } from '../game/squares.js';
import { PIECE_SCALE, SQUARE_SIZE } from '../utils/constants.js';
import { squareToCanvasCenter, type ViewerRotation } from './geometry.js';
import type { RenderPalette } from './palette.js';
import { SHAPE_BOX, pieceShape } from './pieceShapes.js';

export interface PieceDrawOptions {
  readonly alpha?: number;
  /** Multiplier on the resting size (drag lift). */
  readonly scale?: number;
}

const OUTLINE_WIDTH_UNITS = 2.4;
// Dark pieces get a light rim so they separate from the dark squares, which
// are checkers' and close to black in some themes. It is wider than the
// outline drawn over it, so about three units show on each side, and at this
// opacity it is 8.7:1 or better against the dark squares (WCAG 1.4.11 asks 3:1).
const DARK_RIM_WIDTH_UNITS = 8;
const DARK_RIM_COLOR = 'rgba(255, 236, 205, 0.8)';
const SHADOW_OFFSET_UNITS = 3;
const SHADOW_BLUR = 6;

/** Draw a piece (signed code) centred on a canvas point. */
export function drawPiece(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  code: number,
  palette: RenderPalette,
  options: PieceDrawOptions = {},
): void {
  if (code === EMPTY) return;
  const white = code > 0;
  const size = SQUARE_SIZE * PIECE_SCALE * (options.scale ?? 1);
  const unit = size / SHAPE_BOX;
  const shape = pieceShape(typeOf(code));

  ctx.save();
  ctx.globalAlpha = options.alpha ?? 1;
  ctx.translate(x - size / 2, y - size / 2);
  ctx.scale(unit, unit);

  ctx.shadowColor = 'rgba(0, 0, 0, 0.45)';
  ctx.shadowBlur = SHADOW_BLUR;
  ctx.shadowOffsetY = SHADOW_OFFSET_UNITS * unit;

  const grad = ctx.createLinearGradient(20, 0, 80, SHAPE_BOX);
  grad.addColorStop(0, white ? palette.whitePieceHighlight : palette.blackPieceHighlight);
  grad.addColorStop(0.55, white ? palette.whitePieceBase : palette.blackPieceBase);
  grad.addColorStop(1, white ? palette.whitePieceShadow : palette.blackPieceShadow);
  ctx.fillStyle = grad;
  ctx.fill(shape, 'evenodd');

  ctx.shadowColor = 'transparent';
  ctx.lineJoin = 'round';
  if (!white) {
    ctx.lineWidth = DARK_RIM_WIDTH_UNITS;
    ctx.strokeStyle = DARK_RIM_COLOR;
    ctx.stroke(shape);
  }
  ctx.lineWidth = OUTLINE_WIDTH_UNITS;
  ctx.strokeStyle = white ? palette.whitePieceBorder : palette.blackPieceBorder;
  ctx.stroke(shape);
  ctx.restore();
}

/** Draw every piece on the board, skipping squares the caller animates itself. */
export function drawPieces(
  ctx: CanvasRenderingContext2D,
  board: Int8Array,
  palette: RenderPalette,
  rotation: ViewerRotation,
  skip: (sq: number) => boolean = () => false,
): void {
  for (let sq = 0; sq < SQUARE_COUNT; sq++) {
    const code = board[sq] ?? EMPTY;
    if (code === EMPTY || skip(sq)) continue;
    const { x, y } = squareToCanvasCenter(sq, rotation);
    drawPiece(ctx, x, y, code, palette);
  }
}
